import type { dia } from '@joint/core'
import { routeLayerToCategory } from '../../../map-editor-shared/lib/layerUtils'

/**
 * 안내현황판(guideBoard) ↔ 주차구역(parkingLocation) 좌/우 방향 관계.
 *
 * 정책 (사용자 요청): 반경이 아니라 사용자가 드래그한 "왼쪽 영역 / 오른쪽 영역"
 * 박스 안에 든 주차구역으로 좌/우를 정한다. 안내현황판 1개를 선택한 뒤
 * 왼쪽 박스 → leftDirectionIds, 오른쪽 박스 → rightDirectionIds.
 *
 * 대상은 주차구역(zone) 폴리곤이다. 두 키는 GuideBoard export 스키마와 동일하며
 * `*Ids` 패턴이라 relationshipExtractor 가 passthrough 한다.
 */

export const GUIDE_BOARD_LEFT_KEY = 'leftDirectionIds'
export const GUIDE_BOARD_RIGHT_KEY = 'rightDirectionIds'

/** paper(world) 좌표 사각형. 정규화 전이라 x1>x2 일 수 있음. */
export interface WorldRect {
  x1: number
  y1: number
  x2: number
  y2: number
}

export interface GuideBoardDirectionResult {
  guideBoardId: string
  leftDirectionIds: string[]
  rightDirectionIds: string[]
}

/** data.layer → category, 없으면 data.type 으로 폴백 (buildMapData 와 동일 규칙). */
function categoryOf(element: dia.Element): string {
  const data = (element.get('data') as Record<string, unknown>) || {}
  let category = 'unknown'
  if (data.layer) category = routeLayerToCategory(String(data.layer))
  if (category === 'unknown' && data.type) {
    category = routeLayerToCategory(String(data.type))
  }
  return category
}

/** 안내현황판 여부. */
export function isGuideBoardElement(element: dia.Element): boolean {
  return categoryOf(element) === 'guideBoard'
}

/** 주차구역(zone) 여부. */
export function isZoneElement(element: dia.Element): boolean {
  return categoryOf(element) === 'zone'
}

/**
 * 템플릿 relationType 의 source/target 가 안내현황판인지 — 반경 자동관계에서
 * 안내현황판 관계를 제외할 때 쓴다(안내현황판은 좌/우 영역 드래그로만 맺는다).
 */
export function involvesGuideBoard(sourceType?: string, targetType?: string): boolean {
  const cat = (t?: string) => (t ? routeLayerToCategory(t) : 'unknown')
  return cat(sourceType) === 'guideBoard' || cat(targetType) === 'guideBoard'
}

/** 좌상/우하가 뒤집힌 rect 를 min/max 로 정규화. */
export function normalizeRect(rect: WorldRect): WorldRect {
  return {
    x1: Math.min(rect.x1, rect.x2),
    y1: Math.min(rect.y1, rect.y2),
    x2: Math.max(rect.x1, rect.x2),
    y2: Math.max(rect.y1, rect.y2),
  }
}

/** rect(world) 안에 중심점이 든 주차구역(zone) 엘리먼트들. */
export function findZonesInRect(
  graph: dia.Graph,
  rect: WorldRect,
): dia.Element[] {
  const r = normalizeRect(rect)
  return graph.getElements().filter((el) => {
    if (!isZoneElement(el)) return false
    const c = el.getBBox().center()
    return c.x >= r.x1 && c.x <= r.x2 && c.y >= r.y1 && c.y <= r.y2
  })
}

/** immutable 하게 data.properties[key] = ids 설정. */
function setDirectionIds(
  element: dia.Element,
  key: string,
  ids: string[],
): void {
  const data = (element.get('data') as Record<string, unknown>) || {}
  const props = (data.properties as Record<string, unknown>) || {}
  element.set('data', { ...data, properties: { ...props, [key]: ids } })
}

/**
 * 안내현황판에 좌/우 주차구역 id 를 설정한다(replace).
 * 좌우 박스가 겹쳐 같은 구역이 양쪽에 들면 좌측 우선(오른쪽에서 제거)한다.
 */
export function assignGuideBoardDirections(
  guideBoard: dia.Element,
  leftIds: string[],
  rightIds: string[],
): GuideBoardDirectionResult {
  const leftSet = new Set(leftIds)
  const right = rightIds.filter((id) => !leftSet.has(id))
  setDirectionIds(guideBoard, GUIDE_BOARD_LEFT_KEY, leftIds)
  setDirectionIds(guideBoard, GUIDE_BOARD_RIGHT_KEY, right)
  return {
    guideBoardId: String(guideBoard.id),
    leftDirectionIds: leftIds,
    rightDirectionIds: right,
  }
}

/** 화면(screen) 좌표를 paper(world) 좌표로 변환. */
export function screenToWorld(
  paper: dia.Paper,
  clientX: number,
  clientY: number,
): { x: number; y: number } {
  const rect = paper.el.getBoundingClientRect()
  const scale = paper.scale().sx
  const translate = paper.translate()
  return {
    x: (clientX - rect.left - translate.tx) / scale,
    y: (clientY - rect.top - translate.ty) / scale,
  }
}
